/**
 * ============================================================================
 * AstroX - File d'attente IA globale (FIFO)
 * Garantit qu'un seul médium répond à la fois — Premier arrivé, premier servi.
 * Evite la surcharge d'Ollama / Gemini et les réponses simultanées croisées.
 * ============================================================================
 */

type AiTask = {
    sessionId: string;
    mediumId: number | string;
    execute: () => Promise<void>;
    onStart?: () => void;
    onEnd?: () => void;
};

class AiQueue {
    private queue: AiTask[] = [];
    private isProcessing = false;

    /**
     * Ajoute une tâche IA à la file d'attente.
     * Si la file est vide et qu'aucune IA ne tourne, démarre immédiatement.
     * Si une tâche identique (même sessionId) est déjà en attente, elle est remplacée
     * par la dernière version (debounce par session).
     */
    enqueue(task: AiTask): void {
        // Remplacer une tâche existante pour la même session (debounce)
        const existingIdx = this.queue.findIndex(t => t.sessionId === task.sessionId);
        if (existingIdx >= 0) {
            this.queue[existingIdx] = task;
            console.log(`[AI QUEUE] Mise à jour tâche existante pour session ${task.sessionId} (médium ${task.mediumId})`);
        } else {
            this.queue.push(task);
            console.log(`[AI QUEUE] Tâche ajoutée pour session ${task.sessionId} (médium ${task.mediumId}). File d'attente: ${this.queue.length} tâche(s)`);
        }

        if (!this.isProcessing) {
            this.processNext();
        }
    }

    /**
     * Vérifie si une session donnée est actuellement en cours de traitement.
     */
    isSessionProcessing(sessionId: string): boolean {
        return this.isProcessing && this.queue.length > 0 && this.queue[0]?.sessionId === sessionId;
    }

    /**
     * Vérifie si une session est dans la file (en attente ou en traitement).
     */
    isSessionQueued(sessionId: string): boolean {
        return this.queue.some(t => t.sessionId === sessionId);
    }

    /**
     * Annule une tâche en attente pour une session (ex: client a fermé le chat).
     */
    cancel(sessionId: string): void {
        const before = this.queue.length;
        this.queue = this.queue.filter(t => t.sessionId !== sessionId);
        if (this.queue.length < before) {
            console.log(`[AI QUEUE] Tâche annulée pour session ${sessionId}`);
        }
    }

    /**
     * Traite la prochaine tâche dans la file.
     */
    private async processNext(): Promise<void> {
        if (this.queue.length === 0) {
            this.isProcessing = false;
            console.log('[AI QUEUE] File vide. IA disponible.');
            return;
        }

        this.isProcessing = true;
        const task = this.queue.shift()!;

        console.log(`[AI QUEUE] ▶ Démarrage réponse IA pour session ${task.sessionId} (médium ${task.mediumId}). Restant: ${this.queue.length}`);

        try {
            task.onStart?.();
            await task.execute();
        } catch (err) {
            console.error(`[AI QUEUE] [ERR] Erreur lors du traitement de la session ${task.sessionId}:`, err);
        } finally {
            task.onEnd?.();
            // Attendre 200ms entre 2 appels IA pour laisser souffler le modèle
            await new Promise(resolve => setTimeout(resolve, 200));
            this.processNext();
        }
    }

    /**
     * Retourne le nombre de tâches en attente.
     */
    get pendingCount(): number {
        return this.queue.length;
    }

    /**
     * Retourne true si l'IA est occupée.
     */
    get busy(): boolean {
        return this.isProcessing;
    }
}

// Singleton global partagé entre toutes les instances LiveChat
export const aiQueue = new AiQueue();
